import React, { useState, useEffect } from 'react';
import { apiFetch, clearToken, getToken, globalCSS, c } from './api';
import LoginScreen from './views/LoginScreen';
import StorytellerHome from './views/StorytellerHome';
import AdminHome from './views/AdminHome';
import JoinScreen from './views/JoinScreen';
import ViewerHome from './views/ViewerHome';

// ── FamilyAdminHome ───────────────────────────────────────────────────────────
// Family admins can both record stories AND manage their group.
// Toggle between the two views with a tab at the top.

function FamilyAdminHome({ user, onLogout }) {
  const [view, setView] = useState('record');

  const T = {
    bg: '#f4ecd9', paper: '#fdf6e3', ink: '#22180e',
    inkMute: '#a89570', accent: '#c75a2c', divider: 'rgba(34,24,14,0.08)',
    display: "'Newsreader', Georgia, serif", body: "'Work Sans', system-ui, sans-serif",
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      {/* Toggle bar */}
      <div style={{
        display: 'flex', borderBottom: `1px solid ${T.divider}`,
        background: T.paper, position: 'sticky', top: 0, zIndex: 10,
      }}>
        {[['record', 'My Stories'], ['manage', 'My Family']].map(([key, label]) => (
          <button key={key} onClick={() => setView(key)} style={{
            flex: 1, padding: '13px 6px',
            background: 'none', border: 'none',
            borderBottom: view === key ? `2px solid ${T.accent}` : '2px solid transparent',
            fontFamily: T.body, fontSize: 14, fontWeight: view === key ? 600 : 400,
            color: view === key ? T.accent : T.inkMute,
            cursor: 'pointer', transition: 'color 0.15s',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Content — render both but hide inactive to preserve state */}
      <div style={{ display: view === 'record' ? 'block' : 'none' }}>
        <StorytellerHome user={user} onLogout={onLogout} />
      </div>
      <div style={{ display: view === 'manage' ? 'block' : 'none' }}>
        <AdminHome user={user} onLogout={onLogout} />
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null); // { user, adminToken }

  // Check if this is an invite link: /join/TOKEN
  const joinToken = window.location.pathname.startsWith('/join/')
    ? window.location.pathname.split('/join/')[1]
    : null;

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    apiFetch('/auth/me')
      .then(setUser)
      .catch(() => { clearToken(); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const handleLogin = (u) => setUser(u);
  const handleLogout = () => { clearToken(); setUser(null); setImpersonating(null); };

  const handleImpersonate = async (targetUser) => {
    try {
      const adminToken = localStorage.getItem('memories_token');
      const res = await apiFetch('/admin/impersonate/' + targetUser.id, { method: 'POST' });
      localStorage.setItem('memories_token', res.token);
      setImpersonating({ user: targetUser, adminToken });
      setUser(res.user);
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const exitImpersonate = () => {
    localStorage.setItem('memories_token', impersonating.adminToken);
    setUser(null);
    setImpersonating(null);
    apiFetch('/auth/me').then(setUser).catch(() => {});
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f4ecd9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{globalCSS}</style>
        <div style={{ width: 36, height: 36, border: '2px solid rgba(34,24,14,0.08)', borderTop: '2px solid #c75a2c', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  // Invite link — show join screen regardless of auth state
  if (joinToken) {
    return (
      <>
        <style>{globalCSS}</style>
        <JoinScreen token={joinToken} onComplete={(u) => {
          setUser(u);
          window.history.replaceState(null, '', '/');
        }} />
      </>
    );
  }

  return (
    <>
      <style>{globalCSS}</style>
      {impersonating && (
        <div style={{
          position: 'sticky', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#c75a2c', color: '#fdf6e3',
          padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: "'Work Sans', system-ui, sans-serif", fontSize: 14, fontWeight: 600,
        }}>
          <span>👁 Viewing as {impersonating.user.nickname || impersonating.user.name} ({impersonating.user.role})</span>
          <button onClick={exitImpersonate} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fdf6e3', padding: '6px 14px', borderRadius: 99, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            ← Exit — back to admin
          </button>
        </div>
      )}
      {!user && <LoginScreen onLogin={handleLogin} />}
      {user && user.role === 'admin'        && (
        <>
          <AdminHome user={user} onLogout={handleLogout} onImpersonate={handleImpersonate} />
        </>
      )}
      {user && user.role === 'family_admin' && <FamilyAdminHome user={user} onLogout={handleLogout} />}
      {user && user.role === 'storyteller'  && <StorytellerHome user={user} onLogout={handleLogout} />}
      {user && user.role === 'viewer'       && <ViewerHome user={user} onLogout={handleLogout} />}
    </>
  );
}
